import React, { useEffect, useState } from 'react';
import GridFilterPanel from '../../commons/filters/GridFilterPanel.jsx';
import * as Utils from '../../commons/utils/Utils.js';
import {
	INVENTARIO_STOCK_FILTROS_STORAGE_KEY,
	buildInventarioStockFilterFields,
	getInventarioStockFilterDefaults,
} from './inventarioStockFilterFields.js';

/**
 * VEC-3313 — filtros de la grilla de Stock por Depósito.
 *
 * Este componente NO implementa el panel: lo alimenta. El estado, la persistencia y la
 * serialización del query son de <GridFilterPanel /> (commons/filters/); acá solo se cargan las
 * opciones del desplegable de depósitos y se normalizan a `{value, label}`.
 *
 * Es el molde de `MovilesGridFiltros.jsx`, no el de `InventarioManoObraGridFiltros.jsx`: ese último
 * es el panel escrito a mano, anterior al refactor de VEC-3690 / VEC-3698.
 */

const OPTIONS_INIT = { ubicacionId: [] };

/**
 * Se reusa el select del Pañol2 en vez de exponer uno propio: es el mismo catálogo de depósitos
 * (`almacenes`) y ya devuelve SelectOption{value,label}. Mismo criterio que el resto del módulo con
 * categorías, marcas y unidades, y el mismo endpoint que consume el formulario de ingreso de
 * VEC-3314 (InventarioStockIngresoAbm::initAlmacenes).
 */
const URL_ALMACENES = '/panol2/select/almacenes';

/**
 * VEC-3781 Sc. 04 — el buscador de ítems del filtro de Producto/Repuesto.
 *
 * Es el MISMO endpoint que alimenta el select del formulario de ingreso (VEC-3314 Sc. 01), y por eso
 * este filtro se pudo hacer como desplegable buscable y no como texto: acota por búsqueda y tiene
 * tope propio (20 por defecto, 50 máximo). El `/api/items/simple-search` del legacy no sirve — hace
 * un `get()` sin límite, que es el anti-patrón que documenta grid-filter-panel.md, y es la razón por
 * la que el filtro de ítem del historial global de VEC-3315 todavía es un campo de texto.
 */
const URL_ITEMS_SELECT = '/inventario/items/select';

/**
 * El panel lee siempre `option.value` y `option.label`. `/panol2/select/almacenes` ya devuelve esa
 * forma, pero se normaliza igual y se garantiza que el label nunca sea `undefined`: MUI le pide
 * `.toLowerCase()` al filtrar y un label indefinido no deja el desplegable vacío, tira el render de
 * la pantalla entera. Es la trampa que documenta grid-filter-panel.md con /transportadoras/select.
 */
const aOpciones = (data) =>
	(data || [])
		.filter((e) => e && e.value !== undefined && e.value !== null)
		.map((e) => ({ value: e.value, label: e.label === undefined || e.label === null ? String(e.value) : e.label }));

const InventarioStockGridFiltros = (props) => {
	const [options, setOptions] = useState(OPTIONS_INIT);
	const [optionsLoading, setOptionsLoading] = useState(true);

	/**
	 * El panel llama a esto con el texto tipeado (ya con su debounce y su mínimo de 2 caracteres) y
	 * espera un array de `{value, label}` — que es exactamente lo que devuelve el endpoint, así que
	 * no hay que mapear nada.
	 *
	 * Un fallo de red devuelve lista vacía en vez de propagar: el desplegable muestra "sin
	 * resultados", que es un estado que el usuario entiende, en vez de tirar el render del panel.
	 * Y `hayMas` se ignora a propósito — el backend lo sigue devolviendo (contrato documentado y con
	 * test), pero avisar "hay más ítems" en un filtro repetiría el hallazgo que se cerró en el
	 * formulario de ingreso el 04/09 sacando ese aviso entero.
	 */
	const buscarItems = (busqueda) => {
		const texto = busqueda ? String(busqueda).trim() : '';
		const query = texto ? '?busqueda=' + encodeURIComponent(texto) : '';

		return Utils.getData(URL_ITEMS_SELECT + query)
			.then((data) => (data && data.items) || [])
			.catch(() => []);
	};

	const fields = buildInventarioStockFilterFields({ onSearch: buscarItems });

	/**
	 * Las opciones se avisan hacia arriba además de alimentar al panel: la grilla necesita traducir
	 * el id del depósito filtrado a su nombre para el modal del Scenario 05, y el panel solo entrega
	 * el query ya serializado (el `value`, sin el `label`). Sin esto habría que pedir el mismo select
	 * dos veces, o el modal diría "movimientos en el depósito 6".
	 */
	useEffect(() => {
		setOptionsLoading(true);
		Utils.getData(URL_ALMACENES)
			.then((data) => {
				const cargadas = { ubicacionId: aOpciones(data) };
				setOptions(cargadas);
				if (props.onOptionsLoaded) props.onOptionsLoaded(cargadas);
			})
			.catch(() => setOptions(OPTIONS_INIT))
			.finally(() => setOptionsLoading(false));
	}, []);

	return (
		<GridFilterPanel
			open={props.open}
			fields={fields}
			options={options}
			defaults={getInventarioStockFilterDefaults()}
			storageKey={INVENTARIO_STOCK_FILTROS_STORAGE_KEY}
			loading={props.loading || optionsLoading}
			// La grilla es de React/MUI: trae los datos ella misma a partir del query y no tiene de
			// dónde sacarlo al montar. Sin esto, los filtros persistidos se VEN en los controles pero
			// no están aplicados, y el listado sale sin filtrar sin que el usuario lo note.
			applyOnMount={true}
			resetSignal={props.resetSignal}
			onResetSignalHandled={props.onResetSignalHandled}
			onApply={props.onApply}
		/>
	);
};

export default InventarioStockGridFiltros;
